import axios from 'axios';
import axiosInstance from './dchAPI';
import LocalStorageService from './localStorageService';
import eventBus from '../common/EventBus';
import config from '@/config';
import { getTokenExpiryFromJwt } from '@/store/modules/authentication';

const no_token_urls = ['login', 'auth/login', 'auth/exchange/dch'];

let isRefreshing = false;
let failedQueue = [];
let connectionErrorShown = false;
let connectionErrorTimeout = null;

const showConnectionError = () => {
  if (connectionErrorShown) return;

  connectionErrorShown = true;
  eventBus.dispatch('showError', {
    message: 'Unable to connect to the server. Please check your connection or try again later.'
  });

  // Reset after 10 seconds to allow showing the error again if issues persist
  clearTimeout(connectionErrorTimeout);
  connectionErrorTimeout = setTimeout(() => {
    connectionErrorShown = false;
  }, 10000);
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

const setup = (store) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      if (!no_token_urls.includes(config.url)) {
        config.headers['Authorization'] = LocalStorageService.getAccessToken();
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  axiosInstance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
      // Handle cases where err is a string or simple error object
      if (typeof err === 'string' || !err.config) {
        return Promise.reject({
          isGenericError: true,
          message: typeof err === 'string' ? err : err.message || 'An error occurred',
          originalError: err
        });
      }

      const originalConfig = err.config;
      
      // Handle network errors (API is down, ECONNREFUSED, etc.)
      if (err.request && !err.response) {
        // Network error - no response received
        showConnectionError();
        return Promise.reject({
          isNetworkError: true,
          message: 'Unable to connect to the server. Please check your connection or try again later.',
          originalError: err
        });
      }
      
      if (!no_token_urls.includes(originalConfig.url) && err.response) {
        const errorMessage = err.response.data?.message ?? err.response.data;
        const hasConnectionIssue = typeof errorMessage === 'string' && (errorMessage.includes('ECONNREFUSED') || errorMessage.includes('Network Error'));
        if (hasConnectionIssue) {
          showConnectionError();
          return Promise.reject({
            isNetworkError: true,
            message: 'Unable to connect to the server. Please check your connection or try again later.',
            originalError: err
          });
        }
        
        // Check for errors in the standard error format: {errors: [{detail, code, status}]}
        const errorsList = err.response.data?.errors;
        const hasTokenExpiredError = errorsList?.some(
          error => error.detail === 'Token has expired' && (error.code === 'HTTP_401' || error.status === '401')
        );
        
        if (
          err.response.status === 401 &&
          !originalConfig._retry &&
          (errorMessage === 'Unauthorized' || hasTokenExpiredError)
        ) {
          if (isRefreshing) {
            // Token refresh is already in progress, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                originalConfig.headers['Authorization'] = token;
                return axiosInstance(originalConfig);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalConfig._retry = true;
          isRefreshing = true;

          const authRefreshToken = LocalStorageService.getAuthRefreshToken();
          const platformRefreshToken = LocalStorageService.getRefreshToken();

          if (platformRefreshToken) {
            try {
              if (config.ANKERI_AUTH_ENABLED) {
                const dchUrl =
                  process.env.NODE_ENV === 'development'
                    ? '/dchapi/'
                    : config.DCH_API_URL;
                const res = await axios.post(`${dchUrl}auth/refresh/dch`, {
                  refreshToken: platformRefreshToken,
                  platform_url: config.PLATFORM_URL,
                });

                const { token, refreshToken } = res.data;
                const tokenExpiry = token ? getTokenExpiryFromJwt(token) : null;

                store.dispatch('refreshTokenLogin', { token, refreshToken, tokenExpiry });

                processQueue(null, token);
                isRefreshing = false;
                return axiosInstance(originalConfig);
              } else {
                const platformUrl =
                  process.env.NODE_ENV === 'development'
                    ? '/dchapi/'
                    : config.PLATFORM_API_URL;
                const res = await axios.post(`${platformUrl}login/refresh`, {
                  refreshToken: LocalStorageService.getRefreshToken(),
                });

                const { token, refreshToken } = res.data;

                store.dispatch('refreshTokenLogin', { token, refreshToken });

                processQueue(null, token);
                isRefreshing = false;
                return axiosInstance(originalConfig);
              }
            } catch (_error) {
              processQueue(_error, null);
              isRefreshing = false;
              eventBus.dispatch('logout');
              return Promise.reject(_error);
            }
          } else if (authRefreshToken) {
            try {
              const dchUrl =
                process.env.NODE_ENV === 'development'
                  ? '/dchapi/'
                  : config.DCH_API_URL;
              const res = await axios.post(`${dchUrl}auth/refresh`, {
                refreshToken: authRefreshToken,
                ActAsCompanyId: LocalStorageService.getDchCompanyId(),
              });

              const { token, exp: tokenExpiry } = res.data.data;

              store.dispatch('refreshAuth', {
                token,
                tokenExpiry,
              });

              processQueue(null, token);
              isRefreshing = false;
              return axiosInstance(originalConfig);
            } catch (_error) {
              processQueue(_error, null);
              isRefreshing = false;
              eventBus.dispatch('logout');
              return Promise.reject(_error);
            }
          } else {
            // No refresh tokens available, logout user
            processQueue(new Error('No refresh token available'), null);
            isRefreshing = false;
            eventBus.dispatch('logout');
            return Promise.reject(err);
          }
        }
      }
      return Promise.reject(err);
    }
  );
};

export default setup;
